// src/selectors/adminkpiSelectors.ts
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { selectAllTenants } from '../slices/tenantsSlice';

/** Shared types */
export type KPIGroup = { total: number; available: number };
export type AdminDashboardKPIBlocks = {
  tenants: { total: number; active: number };
  sits: KPIGroup;
  labels: KPIGroup;
  policies: KPIGroup;
  overall: number; // %
};

const isAvailValue = (v?: string) => {
  if (typeof v !== 'string') return false;
  const x = v.toLowerCase();
  return x === 'available' || x === 'active' || x === 'enabled';
};

export const selectDashboardKPIBlocks = createSelector(
  [
    (s: RootState) => s.sits.items as any[],
    (s: RootState) => s.labels.data as any[],
    (s: RootState) => s.policies.items as any[],
    selectAllTenants,
  ],
  (sits, labels, policies, tenants): AdminDashboardKPIBlocks => {
    // Build a union of tenant IDs from all datasets as a fallback
    const tenantIds = new Set<string>();
    const collect = (arr: any[]) =>
      arr.forEach((it) => it?.tenants?.forEach((t: any) => tenantIds.add(t.id)));
    collect(sits);
    collect(labels);
    collect(policies);

    const sitsAvail = sits.filter((it) =>
      it?.tenants?.some((t: any) => isAvailValue(t.status)),
    ).length;
    const labelsAvail = labels.filter(
      (it) => it?.tenants?.some((t: any) => isAvailValue(t.status)) || it?.enabled === true,
    ).length;
    const policiesAvail = policies.filter(
      (it) => it?.tenants?.some((t: any) => isAvailValue(t.status)) || it?.enabled === true,
    ).length;

    const totalItems = Math.max(1, sits.length + labels.length + policies.length);
    const availableItems = sitsAvail + labelsAvail + policiesAvail;

    // Prefer loaded tenants slice; if empty, show deduped count from data
    const tenantCount = tenants.length > 0 ? tenants.length : tenantIds.size;

    return {
      tenants: { total: tenantCount, active: tenantCount },
      sits: { total: sits.length, available: sitsAvail },
      labels: { total: labels.length, available: labelsAvail },
      policies: { total: policies.length, available: policiesAvail },
      overall: Math.round((availableItems / totalItems) * 100),
    };
  },
);
